import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Clock, CheckCircle2, AlertCircle, Loader2, ShieldCheck, Flag,
  MapPin, Scissors, CalendarDays, X, UserRound, Pencil,
  ClipboardCheck, CalendarRange, ChevronLeft, ChevronRight, Info,
  Phone, MessageCircle, TrendingUp, UserCog, LifeBuoy, ChevronDown,
  Wallet, XCircle, Hourglass, CalendarPlus, Send, Lock, Bell, Receipt, Eye, Award,
  UserPlus, Search, Plus, Trash2, BarChart3, Shuffle, ArrowRightLeft, Sparkles, Check, Trophy, GraduationCap,
} from "lucide-react";
import { SpotInstructions, SpotAuthorizedBy } from "@/components/SpotInstructions";
import { MaPaie } from "@/components/MaPaie";
import { RecruiterAvatar, rdPhotoSrc } from "@/components/RecruiterAvatar";
import { SpotProofButton } from "@/components/SpotProofDialog";
import { AnnouncementModal } from "@/components/AnnouncementModal";
import { CollabQualite } from "@/components/pointage/qualite";
import { RespDispatch } from "@/components/pointage/dispatch";

import {
  PerfStrip, CollabDashboard, StreakReminderBanner, RatioAlertBanner,
  RatioGreenBanner, WeeklyRecapBanner, LevelUpCelebration,
  usePerfCelebrations,
} from "@/components/pointage/perf";

const fmtH = (h) => (h === null || h === undefined ? "—" : Number(h).toFixed(2).replace(".", ","));

const CONF_LABEL = {
  confirme: { txt: "Confirmé", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
  conteste: { txt: "Contesté", cls: "bg-[rgba(214,69,69,0.10)] text-[#9B2226]" },
  sans_reponse: { txt: "Non confirmé", cls: "bg-[var(--surface-2)] text-muted-ink" },
};

export default function PointageResp() {
  const { token } = useParams();
  const [tab, setTab] = useState("heures");            // "heures" | "planning" | "perf"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [perfStats, setPerfStats] = useState(null);   // perf INDIVIDUELLE du responsable
  const [paie, setPaie] = useState(null);             // relevés de paie (onglet « Ma paie » — conditionnel)
  const [date, setDate] = useState("");
  const [acting, setActing] = useState(null);          // clé de ligne en cours
  const [signalFor, setSignalFor] = useState(null);    // ligne à signaler
  const [motif, setMotif] = useState("");
  const [editFor, setEditFor] = useState(null);        // ligne dont on saisit les heures
  const [editMode, setEditMode] = useState("heures");  // "heures" | "bornes"
  const [editHeures, setEditHeures] = useState("");
  const [editArr, setEditArr] = useState("");
  const [editDep, setEditDep] = useState("");
  const [editFormation, setEditFormation] = useState("");   // dont formation (h), hors BS/h
  const [editMotif, setEditMotif] = useState("");

  const rowKey = (l) => l.pointage_id || l.affectation_id || l.code_rd;

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const q = d ? `?date=${d}` : "";
      const r = await publicApi.get(`/pointage/resp/${token}${q}`);
      setData(r.data);
      if (!d) setDate(r.data.date);
      setError(null);
    } catch (e) {
      setError({ code: e?.response?.status, message: e?.response?.data?.detail || "Accès indisponible." });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Perf INDIVIDUELLE du responsable (mêmes indicateurs gamifiés que les collaborateurs).
  // Chargée une seule fois : alimente le bandeau PerfStrip + l'onglet « Ma perf ».
  useEffect(() => {
    let alive = true;
    publicApi.get(`/pointage/resp/${token}/mes-stats`)
      .then((r) => { if (alive) setPerfStats(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [token]);

  // Relevés de paie du responsable (Reporting). L'onglet « Ma paie » n'apparaît QUE
  // s'il est autorisé ET sa paie publiée (visible:true + releves non vide).
  useEffect(() => {
    let alive = true;
    publicApi.get(`/pointage/resp/${token}/ma-paie`)
      .then((r) => { if (alive) setPaie(r.data); })
      .catch(() => { if (alive) setPaie(null); });
    return () => { alive = false; };
  }, [token]);
  const showPaie = !!(paie && paie.visible && Array.isArray(paie.releves) && paie.releves.length > 0);

  // Pastille « nouveau relevé » sur l'onglet « Ma paie » (voir espace collaborateur).
  const latestPaiePub = showPaie
    ? paie.releves.reduce((m, r) => (r.published_at && (!m || String(r.published_at) > String(m)) ? r.published_at : m), null)
    : null;
  const [paieSeen, setPaieSeen] = useState(null);
  useEffect(() => {
    try { setPaieSeen(localStorage.getItem(`shariti_paie_seen_${token}`)); } catch { /* stockage indisponible */ }
  }, [token]);
  const hasNewPaie = !!(latestPaiePub && latestPaiePub !== paieSeen);
  const markPaieSeen = () => {
    if (!latestPaiePub) return;
    try { localStorage.setItem(`shariti_paie_seen_${token}`, latestPaiePub); } catch { /* stockage indisponible */ }
    setPaieSeen(latestPaiePub);
  };

  const hasPerso = !!(perfStats && perfStats.has_perso);
  const readOnly = !!(data && data.is_readonly);
  const { levelUp, closeLevelUp, showReminder, dismissReminder, showRatioAlert, dismissRatioAlert,
    showRatioGreen, dismissRatioGreen, showRecap, dismissRecap } =
    usePerfCelebrations(token, hasPerso ? perfStats : null);

  const changeDate = (d) => { setDate(d); load(d); };

  const valider = async (ligne) => {
    setActing(rowKey(ligne));
    try {
      await publicApi.post(`/pointage/resp/${token}/valider`, { pointage_id: ligne.pointage_id });
      toast.success(`Heures de ${ligne.rd_name} validées.`);
      await load(date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible.");
    } finally { setActing(null); }
  };

  const submitSignal = async () => {
    if (!signalFor || !motif.trim()) return;
    setActing(rowKey(signalFor));
    try {
      await publicApi.post(`/pointage/resp/${token}/signaler`, { pointage_id: signalFor.pointage_id, motif: motif.trim() });
      toast.success(`Signalement transmis pour ${signalFor.rd_name}.`);
      setSignalFor(null); setMotif("");
      await load(date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible.");
    } finally { setActing(null); }
  };

  const openEdit = (l) => {
    setEditFor(l);
    setEditMode(l.heures_manuelle ? "heures" : "bornes");
    setEditHeures(l.heures_manuelle && l.heures != null ? Number(l.heures).toFixed(2).replace(".", ",") : "");
    setEditArr(l.arrivee_retenue || l.arrivee_pointee || "");
    setEditDep(l.depart_retenu || l.depart_pointe || "");
    setEditFormation(l.heures_formation ? Number(l.heures_formation).toFixed(2).replace(".", ",") : "");
    setEditMotif("");
  };

  const submitSaisie = async () => {
    if (!editFor) return;
    const ident = editFor.pointage_id
      ? { pointage_id: editFor.pointage_id }
      : { affectation_id: editFor.affectation_id, booking_id: editFor.booking_id, rd_id: editFor.rd_id,
          creneau_debut: editFor.creneau_debut, creneau_fin: editFor.creneau_fin };
    const payload = { ...ident, motif: editMotif.trim() || undefined };
    let formationH = 0;
    if ((editFormation || "").trim()) {
      formationH = parseFloat((editFormation || "").replace(",", "."));
      if (isNaN(formationH) || formationH < 0) { toast.error("Formation : nombre d'heures invalide."); return; }
    }
    if (editMode === "heures") {
      const h = parseFloat((editHeures || "").replace(",", "."));
      if (isNaN(h) || h < 0 || h > 24) { toast.error("Nombre d'heures invalide (ex. 4,97)."); return; }
      if (formationH > h) { toast.error("La formation ne peut pas dépasser le total d'heures."); return; }
      payload.heures = h;
    } else {
      if (!editArr && !editDep) { toast.error("Renseignez au moins une borne (arrivée ou départ)."); return; }
      payload.arrivee = editArr || null;
      payload.depart = editDep || null;
    }
    payload.heures_formation = formationH;
    setActing(rowKey(editFor));
    try {
      await publicApi.post(`/pointage/resp/${token}/saisie-heures`, payload);
      toast.success(`Heures enregistrées pour ${editFor.rd_name}.`);
      setEditFor(null);
      await load(date);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible.");
    } finally { setActing(null); }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-subtle)]">
      <div className={`mx-auto flex min-h-screen w-full flex-col px-4 pb-10 pt-6 ${tab === "paie" ? "max-w-md md:max-w-3xl" : (tab === "kpis" || tab === "dispo") ? "max-w-md md:max-w-5xl" : "max-w-md"}`}>
        <header className="mb-5 flex items-center gap-2.5" data-testid="resp-header">
          <img src="/shariti-logo.webp" alt="Shariti" className="h-9 w-9 object-contain" />
          <div className="leading-none">
            <div className="text-[15px] font-extrabold tracking-tight text-[var(--shariti-orange)]">SHARITI</div>
            <div className="text-[9px] font-light tracking-[0.22em] text-muted-ink">VALIDATION DES HEURES</div>
          </div>
        </header>

        {loading && (
          <div className="space-y-3" data-testid="resp-loading">
            <Skeleton className="h-6 w-40" /><Skeleton className="h-24 w-full rounded-2xl" /><Skeleton className="h-24 w-full rounded-2xl" />
          </div>
        )}

        {!loading && error && (
          <div className="mt-10 rounded-2xl border border-[var(--border-soft)] bg-white p-6 text-center shadow-[var(--shadow-sm)]" data-testid="resp-error">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-[rgba(214,69,69,0.10)]">
              <AlertCircle className="h-6 w-6 text-[#9B2226]" />
            </div>
            <h2 className="text-lg font-extrabold text-ink">Accès indisponible</h2>
            <p className="mt-1 text-sm text-muted-ink">{error.message}</p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div className="mb-3" data-testid="resp-identity">
              <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Responsable d'équipe</div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-[var(--shariti-orange)]" />
                <span className="text-xl font-extrabold text-ink" data-testid="resp-name">{data.chef_nom}</span>
                <span className="rounded-md bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px] font-bold text-muted-ink" data-testid="resp-team-size">
                  {data.equipe_taille} collaborateur{data.equipe_taille > 1 ? "s" : ""}
                </span>
              </div>
            </div>

            {/* Bandeau LECTURE SEULE — le responsable peut consulter mais ne peut plus
                corriger/valider les heures (départ / sécurité). */}
            {readOnly && (
              <div className="mb-3 flex items-start gap-2 rounded-2xl border border-[rgba(178,106,0,0.35)] bg-[rgba(238,114,46,0.08)] p-3" data-testid="resp-readonly-banner">
                <Eye className="mt-0.5 h-4 w-4 shrink-0 text-[#B26A00]" />
                <div className="min-w-0">
                  <div className="text-[13px] font-extrabold text-ink">Accès en lecture seule</div>
                  <p className="mt-0.5 text-[12px] leading-snug text-muted-ink">
                    Vous pouvez consulter les heures de votre équipe, mais la correction, la validation et le signalement sont désactivés. Contactez votre administrateur.
                  </p>
                </div>
              </div>
            )}

            {hasPerso && showReminder && (
              <StreakReminderBanner streak={perfStats?.current_streak || 0} onDismiss={dismissReminder} />
            )}

            {/* Alerte ROUGE — ratio de la semaine sous l'objectif (perf perso) — refermable */}
            {hasPerso && showRatioAlert && (
              <RatioAlertBanner ratios={perfStats?.ratios} onDismiss={dismissRatioAlert} />
            )}

            {/* Alerte VERTE — le ratio de la semaine repasse au vert (félicitations) */}
            {hasPerso && showRatioGreen && (
              <RatioGreenBanner ratios={perfStats?.ratios} onDismiss={dismissRatioGreen} />
            )}

            {/* Récap HEBDO — le lundi, bilan de la semaine passée */}
            {hasPerso && showRecap && (
              <WeeklyRecapBanner recap={perfStats?.recap_semaine} onDismiss={dismissRecap} />
            )}

            {/* Bandeau gamifié TOUJOURS visible (perf individuelle) — cliquable → « Ma perf » */}
            {hasPerso && <PerfStrip stats={perfStats} onOpen={() => setTab("perf")} />}

            {/* Onglets : Heures de l'équipe | Planning (mes lignes) | Ma perf (perso)
                | Bonus spot (si le responsable est autorisé à rebooker) */}
            <div className="mb-3 grid grid-cols-4 gap-1 rounded-xl border border-[var(--border-soft)] bg-white p-1 shadow-[var(--shadow-sm)]" data-testid="resp-tabs">
              <button onClick={() => setTab("heures")} data-testid="resp-tab-heures"
                className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "heures" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <ClipboardCheck className="h-4 w-4 shrink-0" /> <span>Heures</span>
              </button>
              <button onClick={() => setTab("planning")} data-testid="resp-tab-planning"
                className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "planning" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <CalendarRange className="h-4 w-4 shrink-0" /> <span>Planning</span>
              </button>
              <button onClick={() => setTab("equipe")} data-testid="resp-tab-equipe"
                className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "equipe" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <UserPlus className="h-4 w-4 shrink-0" /> <span>Équipe</span>
              </button>
              <button onClick={() => setTab("dispatch")} data-testid="resp-tab-dispatch"
                className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "dispatch" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <Shuffle className="h-4 w-4 shrink-0" /> <span>Dispatch</span>
              </button>
              <button onClick={() => setTab("dispo")} data-testid="resp-tab-dispo"
                className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "dispo" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <CalendarPlus className="h-4 w-4 shrink-0" /> <span>Dispos</span>
              </button>
              <button onClick={() => setTab("kpis")} data-testid="resp-tab-kpis"
                className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "kpis" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <BarChart3 className="h-4 w-4 shrink-0" /> <span>KPIs équipe</span>
              </button>
              <button onClick={() => setTab("perf")} data-testid="resp-tab-perf"
                className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "perf" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <TrendingUp className="h-4 w-4 shrink-0" /> <span>Ma perf</span>
              </button>
              {hasPerso && (
                <button onClick={() => setTab("qualite")} data-testid="resp-tab-qualite"
                  className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "qualite" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                  <Award className="h-4 w-4 shrink-0" /> <span>Qualité</span>
                </button>
              )}
              {data.rebook_allowed && (
                <button onClick={() => setTab("primes")} data-testid="resp-tab-primes"
                  className={`flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "primes" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                  <Wallet className="h-4 w-4 shrink-0" /> <span>Bonus spot</span>
                </button>
              )}
              {showPaie && (
                <button onClick={() => { setTab("paie"); markPaieSeen(); }} data-testid="resp-tab-paie"
                  className={`relative flex w-full flex-col items-center justify-center gap-0.5 rounded-lg px-1.5 py-1.5 text-[11px] font-bold leading-tight text-center transition-colors ${tab === "paie" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                  {hasNewPaie && (
                    <span className="absolute right-1.5 top-1 h-2 w-2 rounded-full bg-[var(--shariti-orange)] ring-2 ring-white" data-testid="ma-paie-new-dot" aria-label="Nouveau relevé de paie" />
                  )}
                  <Receipt className="h-4 w-4 shrink-0" /> <span>Ma paie</span>
                </button>
              )}
            </div>

            {tab === "planning" ? (
              <RespPlanning token={token} readOnly={readOnly} />
            ) : tab === "equipe" ? (
              <RespEquipeTab token={token} readOnly={readOnly} onChanged={load} />
            ) : tab === "dispatch" ? (
              <RespDispatch token={token} readOnly={readOnly} />
            ) : tab === "dispo" ? (
              <RespDispoTab token={token} readOnly={readOnly} />
            ) : tab === "kpis" ? (
              <RespKpisTab token={token} />
            ) : tab === "primes" ? (
              <RespPrimesTab token={token} />
            ) : tab === "paie" ? (
              <MaPaie data={paie} />
            ) : tab === "qualite" ? (
              <CollabQualite token={token} qualiteUrl={`/pointage/resp/${token}/qualite`} />
            ) : tab === "perf" ? (
              perfStats && !perfStats.has_perso ? (
                <RespPerfEmpty name={perfStats.rd_name || data.chef_nom} />
              ) : (
                <CollabDashboard token={token} preloaded={perfStats}
                  statsUrl={`/pointage/resp/${token}/mes-stats`} />
              )
            ) : (
            <>
            {/* Sélecteur de jour + compteurs */}
            <div className="mb-3 flex items-center justify-between gap-2 rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]">
              <label className="flex items-center gap-2 text-sm font-semibold text-ink">
                <CalendarDays className="h-4 w-4 text-[var(--shariti-orange)]" />
                <input type="date" value={date} onChange={(e) => changeDate(e.target.value)} data-testid="resp-date" className="rounded-md border border-[var(--border-soft)] px-2 py-1 text-sm font-semibold" />
              </label>
              <div className="flex gap-1.5 text-[10px] font-bold" data-testid="resp-counts">
                <span className="rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[#0F5A2F]">{data.counts.valide} validé{data.counts.valide > 1 ? "s" : ""}</span>
                <span className="rounded-full bg-[rgba(214,69,69,0.10)] px-2 py-0.5 text-[#9B2226]">{data.counts.signale} signalé{data.counts.signale > 1 ? "s" : ""}</span>
                <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-muted-ink">{data.counts.en_attente} en attente</span>
                {data.counts.absences > 0 && <span className="rounded-full bg-[rgba(214,69,69,0.10)] px-2 py-0.5 text-[#9B2226]" data-testid="resp-count-absences">{data.counts.absences} à saisir</span>}
              </div>
            </div>

            {data.lignes.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="resp-empty">
                <Users className="mx-auto mb-2 h-8 w-8 text-muted-ink" />
                <h2 className="text-base font-extrabold text-ink">Aucun pointage ce jour</h2>
                <p className="mt-1 text-sm text-muted-ink">Aucun membre de votre équipe n'a de pointage à cette date.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="resp-list">
                {data.lignes.map((l) => (
                  <RespRow key={rowKey(l)} l={l} acting={acting === rowKey(l)}
                    onValider={() => valider(l)} onSignaler={() => { setSignalFor(l); setMotif(""); }}
                    onEdit={() => openEdit(l)} readOnly={readOnly} />
                ))}
              </div>
            )}

            <p className="mt-6 text-center text-[11px] leading-relaxed text-muted-ink" data-testid="resp-legal">
              {readOnly
                ? "Accès en lecture seule : vous pouvez consulter les heures de votre équipe, mais aucune correction, validation ou signalement n'est possible. Contactez votre administrateur."
                : "En tant que responsable, vous pouvez saisir ou corriger les heures de vos collaborateurs (le pointage brut éventuel reste conservé). Chaque action est tracée (vous, date, heure). Lien personnel — ne le partagez pas."}
            </p>
            </>
            )}
          </>
        )}
      </div>

      {/* Célébration de PASSAGE DE NIVEAU (perf perso du responsable, tous onglets) */}
      {levelUp && <LevelUpCelebration level={levelUp} onClose={closeLevelUp} />}

      {/* Feuille de signalement */}
      {signalFor && (
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" data-testid="resp-signal-sheet">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-[var(--shadow-md)] sm:rounded-2xl">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">Signaler un problème</div>
                <div className="text-lg font-extrabold text-ink">{signalFor.rd_name}</div>
              </div>
              <button onClick={() => setSignalFor(null)} data-testid="resp-signal-close" className="rounded-full p-1 text-muted-ink hover:bg-[var(--surface-2)]"><X className="h-5 w-5" /></button>
            </div>
            <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} data-testid="resp-signal-motif"
              placeholder="Motif obligatoire (ex. horaire incohérent, absence constatée…)" className="min-h-[90px] text-sm" />
            <div className="mt-3 flex flex-col gap-2">
              <Button className="w-full bg-[#9B2226] font-bold hover:bg-[#7f1c1f] disabled:opacity-60"
                disabled={!motif.trim() || acting === signalFor.pointage_id} onClick={submitSignal} data-testid="resp-signal-submit">
                {acting === signalFor.pointage_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Flag className="mr-1.5 h-4 w-4" /> Transmettre le signalement</>}
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setSignalFor(null)} data-testid="resp-signal-cancel">Annuler</Button>
            </div>
          </div>
        </div>
      )}

      {/* Feuille de saisie / correction des heures (responsable) */}
      {editFor && (
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" data-testid="resp-edit-sheet">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-[var(--shadow-md)] sm:rounded-2xl">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-muted-ink">{editFor.pointage_id ? "Corriger les heures" : "Saisir les heures"}</div>
                <div className="text-lg font-extrabold text-ink">{editFor.rd_name}</div>
                <div className="text-[11px] text-muted-ink">{editFor.spot_name} · {editFor.creneau_debut}–{editFor.creneau_fin}</div>
              </div>
              <button onClick={() => setEditFor(null)} data-testid="resp-edit-close" className="rounded-full p-1 text-muted-ink hover:bg-[var(--surface-2)]"><X className="h-5 w-5" /></button>
            </div>
            <div className="mb-3 flex gap-2">
              <button type="button" onClick={() => setEditMode("bornes")} data-testid="resp-edit-mode-bornes"
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${editMode === "bornes" ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[#8a4b1f]" : "border-[var(--border-soft)] text-muted-ink"}`}>Arrivée / Départ</button>
              <button type="button" onClick={() => setEditMode("heures")} data-testid="resp-edit-mode-heures"
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${editMode === "heures" ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[#8a4b1f]" : "border-[var(--border-soft)] text-muted-ink"}`}>Total d'heures</button>
            </div>
            {editMode === "bornes" ? (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-bold uppercase text-muted-ink">Arrivée</label><input type="time" value={editArr} onChange={(e) => setEditArr(e.target.value)} data-testid="resp-edit-arr" className="mt-1 w-full rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm" /></div>
                <div><label className="text-[11px] font-bold uppercase text-muted-ink">Départ</label><input type="time" value={editDep} onChange={(e) => setEditDep(e.target.value)} data-testid="resp-edit-dep" className="mt-1 w-full rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm" /></div>
              </div>
            ) : (
              <div>
                <label className="text-[11px] font-bold uppercase text-muted-ink">Nombre d'heures</label>
                <input inputMode="decimal" value={editHeures} onChange={(e) => setEditHeures(e.target.value)} placeholder="Ex. 4,97" data-testid="resp-edit-heures" className="mt-1 w-full rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm" />
                <p className="mt-1 text-[11px] text-muted-ink">Total travaillé, deux décimales (ex. 4,97).</p>
              </div>
            )}
            <div className="mt-3">
              <label className="text-[11px] font-bold uppercase text-muted-ink">Dont formation <span className="font-normal normal-case">(h, facultatif)</span></label>
              <input inputMode="decimal" value={editFormation} onChange={(e) => setEditFormation(e.target.value)} placeholder="Ex. 4" data-testid="resp-edit-formation" className="mt-1 w-full rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm" />
              <p className="mt-1 text-[11px] text-muted-ink">Comprises dans le total · payées mais <b className="text-ink">exclues du taux BS/h</b> (terrain uniquement).</p>
            </div>
            <Textarea value={editMotif} onChange={(e) => setEditMotif(e.target.value)} data-testid="resp-edit-motif"
              placeholder="Motif (facultatif)" className="mt-3 min-h-[64px] text-sm" />
            <div className="mt-3 flex flex-col gap-2">
              <Button className="w-full bg-[var(--shariti-orange)] font-bold hover:bg-[#d9631f] disabled:opacity-60"
                disabled={acting === rowKey(editFor)} onClick={submitSaisie} data-testid="resp-edit-submit">
                {acting === rowKey(editFor) ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setEditFor(null)} data-testid="resp-edit-cancel">Annuler</Button>
            </div>
          </div>
        </div>
      )}

      {/* Annonces de la Direction — pop-up bloquant (accusé de lecture obligatoire) */}
      <AnnouncementModal token={token} scope="resp" />
    </div>
  );
}

// Onglet « Équipe » du responsable HABILITÉ : il consulte ses membres et peut
// rattacher lui-même un collaborateur EXISTANT (autonomie, sans passer par l'admin).
// En lecture seule, les actions sont désactivées (consultation uniquement).
// Onglet « Disponibilités » du responsable : grille datée (collaborateurs × 7 jours),
// navigation par semaine (jusqu'à +8), copie de la semaine précédente. Les horaires
// déclarés ici sont repris lors du remplissage automatique du planning. Lecture seule
// respectée. Aucune donnée n'est écrite hors de la fenêtre autorisée (garde-fous backend).
const DISPO_WD_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function dispoFmtJour(iso) {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function RespDispoTab({ token, readOnly = false }) {
  const [week, setWeek] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);     // "rd_id|date" en cours d'enregistrement
  const [editCell, setEditCell] = useState(null);
  const [copying, setCopying] = useState(false);
  const [view, setView] = useState("grille");       // "grille" (saisie) | "couverture"
  const [q, setQ] = useState("");                    // recherche RD dans la grille
  // Point 4 : recherche serveur pour retrouver/rattacher un collaborateur absent du roster
  // (ex. une recrue fraîchement synchronisée depuis le booking).
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const loadWeek = useCallback(async (wk) => {
    setLoading(true);
    try {
      const q = wk ? `?week=${wk}` : "";
      const r = await publicApi.get(`/pointage/resp/${token}/disponibilites${q}`);
      setData(r.data);
      setWeek(r.data.week_monday);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement impossible.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadWeek(""); }, [loadWeek]);

  const canEdit = !!(data && data.editable && !readOnly);
  const atMin = !!(data && week <= data.min_monday);
  const atMax = !!(data && week >= data.max_monday);
  const cellOf = (rid, dt) => (data?.cells?.[rid]?.[dt]) || null;

  // Point 4 : recherche (debounce) de collaborateurs à rattacher — inclut les recrues
  // fraîchement remontées via la synchro booking (hors roster actuel).
  const rosterIds = new Set((data?.roster || []).map((r) => r.rd_id));
  useEffect(() => {
    if (!canEdit) { setAddResults([]); setAddSearching(false); return; }
    const term = q.trim();
    if (term.length < 2) { setAddResults([]); setAddSearching(false); return; }
    let active = true;
    setAddSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await publicApi.get(`/pointage/resp/${token}/membres/recherche?q=${encodeURIComponent(term)}`);
        if (active) setAddResults(r.data?.results || []);
      } catch { if (active) setAddResults([]); }
      finally { if (active) setAddSearching(false); }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [q, token, canEdit]);

  const addMember = async (rd) => {
    if (rd.deja_autre_equipe) {
      toast.error("Déjà dans une autre équipe — contactez l'administrateur.");
      return;
    }
    setAddingId(rd.rd_id);
    try {
      await publicApi.post(`/pointage/resp/${token}/membres`, { rd_id: rd.rd_id });
      toast.success(`${rd.name} ajouté(e) à votre équipe`);
      setQ(""); setAddResults([]);
      await loadWeek(data.week_monday);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajout impossible.");
    } finally { setAddingId(null); }
  };

  const openEdit = (r, day) => {
    if (!canEdit) return;
    const c = cellOf(r.rd_id, day.date);
    setEditCell({
      rd_id: r.rd_id, name: r.name, date: day.date,
      dayLabel: `${day.label} ${dispoFmtJour(day.date)}`,
      mode: c?.mode || "dispo",
      debut: c?.debut || data.default_debut,
      fin: c?.fin || data.default_fin,
      note: c?.note || "",
    });
  };

  const saveCell = async (payload) => {
    setSaving(`${payload.rd_id}|${payload.date}`);
    try {
      await publicApi.post(`/pointage/resp/${token}/disponibilites`, payload);
      setData((prev) => {
        if (!prev) return prev;
        const cells = { ...(prev.cells || {}) };
        const row = { ...(cells[payload.rd_id] || {}) };
        if (payload.mode === "none") delete row[payload.date];
        else row[payload.date] = { mode: payload.mode, debut: payload.debut, fin: payload.fin, note: payload.note };
        cells[payload.rd_id] = row;
        return { ...prev, cells };
      });
      setEditCell(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible.");
    } finally { setSaving(null); }
  };

  const copyPrev = async () => {
    if (!canEdit || !data) return;
    setCopying(true);
    try {
      const r = await publicApi.post(`/pointage/resp/${token}/disponibilites/copier`, {
        from_week: data.prev_week, to_week: data.week_monday,
      });
      toast.success(`${r.data.copied} disponibilité(s) copiée(s) depuis la semaine précédente`);
      await loadWeek(data.week_monday);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copie impossible.");
    } finally { setCopying(false); }
  };

  if (loading && !data) {
    return (
      <div className="space-y-2" data-testid="resp-dispo-loading">
        <Skeleton className="h-12 w-full" /><Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="resp-dispo">
      <div className="mb-3 flex items-center justify-between gap-2 rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]">
        <button onClick={() => !atMin && loadWeek(data.prev_week)} disabled={atMin}
          className="rounded-lg border border-[var(--border-soft)] p-2 text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-40"
          data-testid="resp-dispo-prev" aria-label="Semaine précédente"><ChevronLeft className="h-4 w-4" /></button>
        <div className="text-center">
          <div className="text-sm font-extrabold text-ink">Semaine du {dispoFmtJour(data.week_monday)}</div>
          <div className="text-[11px] text-muted-ink">Modifiable jusqu'à {data.horizon_weeks} semaines à l'avance</div>
        </div>
        <button onClick={() => !atMax && loadWeek(data.next_week)} disabled={atMax}
          className="rounded-lg border border-[var(--border-soft)] p-2 text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-40"
          data-testid="resp-dispo-next" aria-label="Semaine suivante"><ChevronRight className="h-4 w-4" /></button>
      </div>

      {!canEdit && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 text-[11px] text-muted-ink" data-testid="resp-dispo-readonly">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {readOnly ? "Votre accès est en lecture seule." : "Cette semaine est hors de la fenêtre modifiable (semaine en cours → +8 semaines)."}
        </div>
      )}

      {canEdit && (
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-[11px] text-muted-ink">Touchez une case pour définir la disponibilité et les horaires.</p>
          <Button variant="secondary" size="sm" onClick={copyPrev} disabled={copying} data-testid="resp-dispo-copy">
            {copying ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CalendarRange className="mr-1.5 h-3.5 w-3.5" />}
            Copier la semaine précédente
          </Button>
        </div>
      )}

      {/* Bascule Saisie / Couverture + recherche */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1.5">
          <button type="button" onClick={() => setView("grille")} data-testid="resp-dispo-view-grille"
            className={`rounded-lg border px-3 py-1.5 text-[11px] font-bold transition-colors ${view === "grille" ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[#8a4b1f]" : "border-[var(--border-soft)] text-muted-ink hover:bg-[var(--surface-2)]"}`}>
            Mes dispos
          </button>
          <button type="button" onClick={() => setView("couverture")} data-testid="resp-dispo-view-couverture"
            className={`rounded-lg border px-3 py-1.5 text-[11px] font-bold transition-colors ${view === "couverture" ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[#8a4b1f]" : "border-[var(--border-soft)] text-muted-ink hover:bg-[var(--surface-2)]"}`}>
            Couverture
          </button>
        </div>
        {view === "grille" && (
          <div className="relative ml-auto min-w-[200px] flex-1 sm:max-w-[320px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher ou ajouter un collaborateur…"
              className="w-full rounded-lg border border-[var(--border-soft)] py-1.5 pl-8 pr-2 text-sm" data-testid="resp-dispo-search" />
          </div>
        )}
      </div>

      {/* Point 4 — Résultats de rattachement : collaborateurs correspondants PAS encore
          dans l'équipe (ex. recrue fraîchement synchronisée). Permet de les ajouter ici. */}
      {view === "grille" && canEdit && q.trim().length >= 2
        && (addSearching || addResults.some((r) => !rosterIds.has(r.rd_id))) && (
        <div className="mb-3 rounded-2xl border border-[rgba(238,114,46,0.25)] bg-[rgba(238,114,46,0.05)] p-3" data-testid="resp-dispo-add-panel">
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#8a4b1f]">
            <UserPlus className="h-3.5 w-3.5" />Ajouter à mon équipe
          </div>
          {addSearching && addResults.length === 0 ? (
            <Skeleton className="h-10 w-full rounded-xl" />
          ) : (
            <div className="space-y-1.5">
              {addResults.filter((rd) => !rosterIds.has(rd.rd_id)).map((rd) => (
                <div key={rd.rd_id} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] bg-white px-3 py-2" data-testid={`resp-dispo-add-${rd.code}`}>
                  <div className="min-w-0">
                    <span className="font-semibold text-ink">{rd.name}</span>
                    <span className="ml-1 font-mono text-[11px] text-muted-ink">{rd.code}</span>
                    {rd.deja_autre_equipe && <div className="text-[10px] font-semibold text-[#9B2226]">Déjà dans une autre équipe</div>}
                  </div>
                  <Button size="sm" className="h-7 shrink-0 gap-1 px-2 text-[11px]" onClick={() => addMember(rd)}
                    disabled={addingId === rd.rd_id || rd.deja_autre_equipe} data-testid={`resp-dispo-add-btn-${rd.code}`}>
                    {addingId === rd.rd_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" />Rattacher</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "grille" ? (
      data.roster.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="resp-dispo-empty">
          <Users className="mx-auto mb-2 h-8 w-8 text-muted-ink" />
          <h2 className="text-base font-extrabold text-ink">Aucun collaborateur</h2>
          <p className="mt-1 text-sm text-muted-ink">Utilisez la recherche ci-dessus pour rattacher un collaborateur, ou ajoutez des membres dans l'onglet « Équipe ».</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid="resp-dispo-grid">
          <table className="w-full border-collapse text-[11px]">
            <thead>
              <tr className="bg-[var(--surface-2)]">
                <th className="sticky left-0 z-10 min-w-[128px] bg-[var(--surface-2)] px-2 py-2 text-left font-bold text-ink">Collaborateur</th>
                {data.days.map((d) => (
                  <th key={d.date} className="min-w-[64px] px-1 py-2 text-center font-bold text-ink">
                    <div className="text-[12px]">{DISPO_WD_SHORT[d.weekday]}</div>
                    <div className="text-[10px] font-semibold text-muted-ink">{dispoFmtJour(d.date)}</div>
                    {d.has_emplacement && (
                      <div className="mx-auto mt-0.5 flex items-center justify-center text-[var(--shariti-orange)]" title="Un emplacement est posé ce jour">
                        <MapPin className="h-2.5 w-2.5" />
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.roster.filter((r) => !q.trim() || (r.name || "").toLowerCase().includes(q.trim().toLowerCase())).map((r) => (
                <tr key={r.rd_id} className="border-t border-[var(--border-soft)]">
                  <td className="sticky left-0 z-10 bg-white px-2 py-2 font-semibold text-ink">
                    <div className="flex items-center gap-1">
                      <span className="max-w-[160px] truncate" title={r.name}>{r.name}</span>
                      {r.is_self && <span className="rounded-full bg-[rgba(238,114,46,0.14)] px-1.5 py-0.5 text-[8px] font-bold text-[var(--shariti-orange)]">MOI</span>}
                    </div>
                  </td>
                  {data.days.map((d) => {
                    const c = cellOf(r.rd_id, d.date);
                    const key = `${r.rd_id}|${d.date}`;
                    const isSaving = saving === key;
                    let label = "—", cls = "text-muted-ink bg-white";
                    if (c?.mode === "dispo") { label = `${c.debut}–${c.fin}`; cls = "text-[#0F5A2F] bg-[rgba(31,157,85,0.10)] font-bold"; }
                    else if (c?.mode === "indispo") { label = "Indispo"; cls = "text-[#9B2226] bg-[rgba(214,69,69,0.08)] font-semibold"; }
                    return (
                      <td key={d.date} className="px-1 py-1 text-center align-middle">
                        <button type="button" disabled={!canEdit || isSaving} onClick={() => openEdit(r, d)}
                          data-testid={`resp-dispo-cell-${r.rd_id}-${d.date}`}
                          className={`min-h-[32px] w-full rounded-md border border-[var(--border-soft)] px-1 py-1 text-[10px] leading-tight transition-colors ${cls} ${canEdit ? "hover:ring-1 hover:ring-[var(--shariti-orange)]" : "cursor-default"}`}>
                          {isSaving ? <Loader2 className="mx-auto h-3 w-3 animate-spin" /> : label}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      ) : (
        /* ─── Vue COUVERTURE : par jour, emplacements + personnes placées (rouge si vide),
              + membres disponibles NON encore placés ce jour-là. ─── */
        <div className="space-y-3" data-testid="resp-dispo-couverture">
          {data.days.filter((d) => (d.emplacements || []).length > 0 || (d.available_not_placed || []).length > 0).length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="resp-dispo-couverture-empty">
              <CalendarDays className="mx-auto mb-2 h-8 w-8 text-muted-ink" />
              <h2 className="text-base font-extrabold text-ink">Rien à afficher cette semaine</h2>
              <p className="mt-1 text-sm text-muted-ink">Aucun emplacement posé et aucune disponibilité saisie.</p>
            </div>
          ) : (
            data.days.map((d) => {
              const empls = d.emplacements || [];
              const avail = d.available_not_placed || [];
              if (empls.length === 0 && avail.length === 0) return null;
              return (
                <div key={d.date} className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid={`resp-cover-day-${d.date}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-extrabold text-ink">{d.label} {dispoFmtJour(d.date)}</div>
                    {d.gaps > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(214,69,69,0.10)] px-2 py-0.5 text-[10px] font-bold text-[#9B2226]">
                        <AlertCircle className="h-3 w-3" /> {d.gaps} sans personne
                      </span>
                    )}
                  </div>
                  {empls.length === 0 ? (
                    <p className="text-[12px] text-muted-ink">Aucun emplacement posé ce jour.</p>
                  ) : (
                    <div className="space-y-2">
                      {empls.map((e) => (
                        <div key={e.booking_id}
                          className={`rounded-xl border p-2.5 ${e.count === 0 ? "border-[rgba(214,69,69,0.45)] bg-[rgba(214,69,69,0.06)]" : "border-[var(--border-soft)] bg-[var(--surface-2)]"}`}
                          data-testid={`resp-cover-empl-${e.booking_id}`}>
                          <div className="flex items-center gap-1.5">
                            <MapPin className={`h-3.5 w-3.5 shrink-0 ${e.count === 0 ? "text-[#9B2226]" : "text-[var(--shariti-orange)]"}`} />
                            <span className="truncate text-[13px] font-bold text-ink" title={e.spot_name}>{e.spot_name}</span>
                          </div>
                          {e.count === 0 ? (
                            <div className="mt-1 text-[11px] font-semibold text-[#9B2226]">Aucune personne positionnée</div>
                          ) : (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {e.people.map((p) => (
                                <span key={p.rd_id} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-ink ring-1 ring-[var(--border-soft)]">
                                  {p.rd_name}{p.is_self ? " (moi)" : ""}
                                  {p.debut && p.fin ? <span className="text-[10px] text-muted-ink">· {p.debut}–{p.fin}</span> : null}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {avail.length > 0 && (
                    <div className="mt-2 rounded-xl border border-dashed border-[var(--border-soft)] p-2" data-testid={`resp-cover-avail-${d.date}`}>
                      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">Disponibles non placés</div>
                      <div className="flex flex-wrap gap-1.5">
                        {avail.map((p) => (
                          <span key={p.rd_id} className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.08)] px-2 py-0.5 text-[11px] font-semibold text-[#0F5A2F]">
                            {p.name}{p.debut && p.fin ? <span className="text-[10px] opacity-80">· {p.debut}–{p.fin}</span> : null}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      <Dialog open={!!editCell} onOpenChange={(o) => !o && setEditCell(null)}>
        <DialogContent className="max-w-sm" data-testid="resp-dispo-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{editCell?.name}</DialogTitle>
            <DialogDescription>{editCell?.dayLabel}</DialogDescription>
          </DialogHeader>
          {editCell && (
            <div className="space-y-3">
              <div className="flex gap-1.5">
                {[["dispo", "Disponible"], ["indispo", "Indisponible"], ["none", "Non défini"]].map(([m, lbl]) => (
                  <button key={m} type="button" onClick={() => setEditCell((p) => ({ ...p, mode: m }))}
                    data-testid={`resp-dispo-mode-${m}`}
                    className={`flex-1 rounded-lg border px-2 py-1.5 text-[11px] font-bold transition-colors ${editCell.mode === m ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)] text-[#8a4b1f]" : "border-[var(--border-soft)] text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {editCell.mode === "dispo" && (
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[11px] font-semibold text-muted-ink">Début
                    <input type="time" value={editCell.debut} onChange={(e) => setEditCell((p) => ({ ...p, debut: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-[var(--border-soft)] px-2 py-1.5 text-sm" data-testid="resp-dispo-debut" />
                  </label>
                  <label className="text-[11px] font-semibold text-muted-ink">Fin
                    <input type="time" value={editCell.fin} onChange={(e) => setEditCell((p) => ({ ...p, fin: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-[var(--border-soft)] px-2 py-1.5 text-sm" data-testid="resp-dispo-fin" />
                  </label>
                </div>
              )}
              <Textarea placeholder="Note (optionnel)" value={editCell.note} maxLength={200}
                onChange={(e) => setEditCell((p) => ({ ...p, note: e.target.value }))}
                className="min-h-[60px] text-sm" data-testid="resp-dispo-note" />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditCell(null)} data-testid="resp-dispo-cancel">Annuler</Button>
            <Button disabled={!!saving}
              onClick={() => saveCell({ rd_id: editCell.rd_id, date: editCell.date, mode: editCell.mode, debut: editCell.debut, fin: editCell.fin, note: editCell.note })}
              data-testid="resp-dispo-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function RespEquipeTab({ token, readOnly = false, onChanged }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await publicApi.get(`/pointage/resp/${token}/membres`);
      setInfo(r.data);
    } catch { toast.error("Chargement de l'équipe impossible."); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Recherche (debounce) de collaborateurs existants à rattacher.
  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      setSearching(true);
      try {
        const r = await publicApi.get(`/pointage/resp/${token}/membres/recherche?q=${encodeURIComponent(q.trim())}`);
        if (active) setResults(r.data?.results || []);
      } catch { if (active) setResults([]); }
      finally { if (active) setSearching(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, token]);

  const add = async (rd) => {
    if (rd.deja_autre_equipe) {
      toast.error("Déjà rattaché à une autre équipe. Contactez l'administrateur.");
      return;
    }
    setBusy(rd.rd_id);
    try {
      await publicApi.post(`/pointage/resp/${token}/membres`, { rd_id: rd.rd_id });
      toast.success(`${rd.name} rattaché(e) à votre équipe.`);
      setQ(""); setResults([]);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ajout impossible."); }
    finally { setBusy(null); }
  };

  const remove = async (m) => {
    setBusy(m.rd_id);
    try {
      await publicApi.post(`/pointage/resp/${token}/membres/${m.rd_id}/retirer`, {});
      toast.success(`${m.name} détaché(e) de votre équipe.`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retrait impossible."); }
    finally { setBusy(null); }
  };

  if (loading) {
    return <div className="space-y-2" data-testid="resp-equipe-loading">{[0, 1].map((i) => <Skeleton key={i} className="h-14 w-full rounded-2xl" />)}</div>;
  }

  return (
    <div className="space-y-4" data-testid="resp-equipe-tab">
      <div className="flex items-start gap-2 rounded-2xl border border-[var(--border-soft)] bg-white p-3 text-[12px] leading-snug text-muted-ink shadow-[var(--shadow-sm)]">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
        <span>Ajoutez vous-même un nouveau membre à votre équipe pour pouvoir saisir et valider ses heures — sans attendre l'administrateur.</span>
      </div>

      {/* Ajouter un membre (recherche) */}
      {!readOnly && (
        <div data-testid="resp-equipe-add">
          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-ink">Ajouter un membre</div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom ou matricule AT0…)…"
              className="h-10 w-full rounded-lg border border-[var(--border-soft)] bg-white pl-9 pr-3 text-sm outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
              data-testid="resp-equipe-search" />
          </div>
          {q.trim() && (
            <div className="mt-1 max-h-56 space-y-1 overflow-y-auto" data-testid="resp-equipe-results">
              {searching ? <Skeleton className="h-10 w-full rounded-xl" />
                : results.length === 0 ? <div className="py-3 text-center text-[13px] text-muted-ink">Aucun collaborateur trouvé.</div>
                  : results.map((rd) => (
                    <div key={rd.rd_id} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] px-3 py-2 hover:bg-[var(--surface-2)]" data-testid={`resp-equipe-result-${rd.code}`}>
                      <div className="flex min-w-0 items-center gap-2">
                        <RecruiterAvatar name={rd.name} src={rdPhotoSrc(rd)} size={32} testid={`resp-equipe-result-avatar-${rd.code}`} />
                        <div className="min-w-0">
                          <span className="break-words font-semibold text-ink">{rd.name}</span>
                          <span className="ml-1 font-mono text-[11px] text-muted-ink">{rd.code}</span>
                          {rd.deja_autre_equipe && <div className="text-[10px] font-semibold text-[#9B2226]">Déjà dans une autre équipe</div>}
                        </div>
                      </div>
                      <Button size="sm" className="h-7 shrink-0 gap-1 px-2 text-[11px]" onClick={() => add(rd)}
                        disabled={busy === rd.rd_id || rd.deja_autre_equipe} data-testid={`resp-equipe-add-${rd.code}`}>
                        {busy === rd.rd_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" />Rattacher</>}
                      </Button>
                    </div>
                  ))}
            </div>
          )}
        </div>
      )}

      {/* Liste des membres */}
      <div>
        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-ink">Mes membres ({info?.member_count ?? 0})</div>
        <div className="space-y-1" data-testid="resp-equipe-list">
          {(info?.membres || []).length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-ink">Aucun membre rattaché pour l'instant.</div>
          ) : info.membres.map((m) => (
            <div key={m.rd_id} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] px-3 py-2" data-testid={`resp-equipe-membre-${m.code}`}>
              <div className="min-w-0 flex items-center gap-2">
                <RecruiterAvatar name={m.name} src={rdPhotoSrc(m)} size={34} testid={`resp-equipe-membre-avatar-${m.code}`} />
                <span className="break-words font-semibold text-ink">{m.name}</span>
                <span className="font-mono text-[11px] text-muted-ink">{m.code}</span>
              </div>
              {!readOnly && (
                <Button variant="ghost" size="sm" className="h-7 shrink-0 gap-1 px-2 text-[11px] text-[#9B2226] hover:bg-[rgba(214,69,69,0.10)]"
                  onClick={() => remove(m)} disabled={busy === m.rd_id} data-testid={`resp-equipe-remove-${m.code}`}>
                  {busy === m.rd_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5" />Retirer</>}
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Onglet « KPIs équipe » du responsable ──────────────────────────────────
// Indicateurs AGRÉGÉS de toute l'équipe + détail PAR collaborateur, sur un horizon
// navigable (Jour / Semaine / Mois / Trimestre / Année / Depuis le début). Données :
// /pointage/resp/{token}/kpis (mêmes définitions que l'onglet « Qualité » : taux réel,
// welcome call, don moyen, âge moyen, attrition & pré-attrition via Reporting).
const KPI_PERIODS = [
  { value: "day", label: "Jour" },
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
  { value: "quarter", label: "Trimestre" },
  { value: "year", label: "Année" },
  { value: "all", label: "Tout" },
];
const _kpiPct = (r) => (r == null ? "—" : `${Math.round(r * 100)} %`);
const _kpiEur = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} €`);
const _kpiNum = (v) => (v == null ? "—" : String(v));
const _kpiAge = (v) => (v == null ? "—" : `${String(v).replace(".", ",")} ans`);
// TAUX RÉEL = ratio de productivité = bulletins nets / heures (ex. 0,50 = 2 BS en 4 h).
const _kpiRatio = (v) => (v == null ? "—" : Number(v).toFixed(2).replace(".", ","));
const _kpiHeures = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} h`);
const _wcTone = (r) => (r == null ? "default" : r <= 0.15 ? "good" : r <= 0.25 ? "warn" : "bad");
const _attrTone = (r, t) => (r == null ? "default" : r <= t ? "good" : r <= t * 1.5 ? "warn" : "bad");

function KpiTile({ label, value, sub, tone = "default", testid }) {
  const toneCls = {
    default: "border-[var(--border-soft)] bg-white",
    good: "border-[rgba(31,157,85,0.40)] bg-[rgba(31,157,85,0.06)]",
    warn: "border-[rgba(234,179,8,0.45)] bg-[rgba(234,179,8,0.08)]",
    bad: "border-[rgba(155,34,38,0.40)] bg-[rgba(155,34,38,0.06)]",
    accent: "border-[rgba(238,114,46,0.40)] bg-[rgba(238,114,46,0.06)]",
  }[tone] || "border-[var(--border-soft)] bg-white";
  return (
    <div className={`min-w-0 rounded-xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-muted-ink">{label}</div>
      <div className="mt-0.5 truncate text-lg font-extrabold leading-tight text-ink">{value}</div>
      {sub ? <div className="mt-0.5 truncate text-[10px] text-muted-ink">{sub}</div> : null}
    </div>
  );
}

function KpiMemberStat({ label, value, tone = "default" }) {
  const c = { default: "text-ink", good: "text-[#0F5A2F]", warn: "text-[#8A6D1B]", bad: "text-[#9B2226]" }[tone] || "text-ink";
  return (
    <div className="flex items-center justify-between gap-1 rounded-lg bg-[var(--surface-2)] px-2 py-1">
      <span className="truncate text-muted-ink">{label}</span>
      <span className={`shrink-0 font-bold ${c}`}>{value}</span>
    </div>
  );
}

// ── Podium de l'équipe ──────────────────────────────────────────────────────
// Mini-carte gamifiée : met en avant le visage du Top 3 des collaborateurs par
// nombre de bulletins comptés sur la période. Anneaux or / argent / bronze.
// Ne s'affiche qu'avec au moins 2 collaborateurs ACTIFS ayant produit des bulletins.
const PODIUM_META = [
  { ring: "#E9B800", bg: "rgba(233,184,0,0.14)", ped: "h-16", av: 66 }, // 1er
  { ring: "#9AA3AF", bg: "rgba(154,163,175,0.18)", ped: "h-11", av: 56 }, // 2e
  { ring: "#C77F3B", bg: "rgba(199,127,59,0.16)", ped: "h-8", av: 56 }, // 3e
];
function TeamPodium({ members }) {
  const ranked = (members || [])
    .filter((m) => !m.ex_membre && (m.bulletins_comptes || 0) > 0)
    .sort((a, b) => (b.bulletins_comptes || 0) - (a.bulletins_comptes || 0));
  const top = ranked.slice(0, 3);
  if (top.length < 2) return null;
  // Ordre visuel « podium » : 2 · 1 · 3 (le 1er, plus grand, au centre).
  const visual = top.length >= 3 ? [top[1], top[0], top[2]] : [top[1], top[0]];
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid="kpis-podium">
      <div className="mb-3 flex items-center gap-1.5 text-sm font-extrabold text-ink">
        <Trophy className="h-4 w-4 shrink-0 text-[#E9B800]" />
        <span>Podium de l'équipe</span>
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-muted-ink">Top bulletins</span>
      </div>
      <div className="flex items-end justify-center gap-3 sm:gap-5">
        {visual.map((m) => {
          const rank = top.indexOf(m); // 0, 1, 2
          const mt = PODIUM_META[rank];
          return (
            <div key={m.rd_id} className="flex min-w-0 flex-1 flex-col items-center" style={{ maxWidth: 128 }} data-testid={`kpi-podium-rank-${rank + 1}`}>
              <div className="relative shrink-0 rounded-full" style={{ padding: 3, background: mt.ring }}>
                <RecruiterAvatar name={m.rd_name} src={rdPhotoSrc(m)} size={mt.av} className="ring-2 ring-white" testid={`kpi-podium-avatar-${rank + 1}`} />
                <span className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-full border-2 border-white text-[11px] font-extrabold text-white shadow-[var(--shadow-sm)]" style={{ background: mt.ring }}>{rank + 1}</span>
              </div>
              <div className="mt-2 w-full break-words text-center text-[12px] font-bold leading-tight text-ink">{m.rd_name}</div>
              <div className="mt-0.5 text-[11px] font-extrabold text-[var(--shariti-orange)]">{_kpiNum(m.bulletins_comptes)} BS</div>
              <div className={`mt-2 w-full rounded-t-lg ${mt.ped}`} style={{ background: mt.bg, borderTop: `3px solid ${mt.ring}` }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RespKpisTab({ token }) {
  const [period, setPeriod] = useState("month");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attrLoading, setAttrLoading] = useState(false);
  useEffect(() => { setOffset(0); }, [period]);
  useEffect(() => {
    let alive = true;
    setLoading(true); setAttrLoading(false);
    // Phase 1 — KPIs CŒUR (Mongo, instantané), sans attrition.
    publicApi.get(`/pointage/resp/${token}/kpis?period=${period}&offset=${offset}&attrition=0`)
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        setLoading(false);
        // Phase 2 — ATTRITION (appels Reporting, en arrière-plan) : on complète sans bloquer.
        if ((r.data?.equipe_taille || 0) > 0) {
          setAttrLoading(true);
          publicApi.get(`/pointage/resp/${token}/kpis?period=${period}&offset=${offset}&attrition=1`)
            .then((r2) => { if (alive) setData(r2.data); })
            .catch(() => {})
            .finally(() => { if (alive) setAttrLoading(false); });
        }
      })
      .catch(() => { if (alive) { setData(null); setLoading(false); } });
    return () => { alive = false; };
  }, [token, period, offset]);

  const team = data?.team || {};
  const members = data?.members || [];
  const attr = team.attrition || {};
  const t3 = attr.target_3m ?? 0.11;
  const t12 = attr.target_12m ?? 0.30;
  const attrVal = (ok, val) => (attrLoading ? "…" : ok ? _kpiPct(val) : "—");
  const attrSub = (ok, target) => (attrLoading ? "chargement…" : ok ? `cible ≤ ${_kpiPct(target)}` : "recul insuffisant");
  // Affichage attrition PAR COLLABORATEUR : « … » pendant le chargement Reporting,
  // « — » si recul insuffisant / pas de donnée, sinon le taux. (Même logique que le global,
  // mais toujours affiché pour chaque personne, comme demandé.)
  const mAttrVal = (ma, val) => {
    if (!ma || ma.pending) return attrLoading ? "…" : "—";
    return ma.reliable ? _kpiPct(val) : "—";
  };
  const mAttrTone = (ma, val, target) => (ma && ma.reliable ? _attrTone(val, target) : "default");

  return (
    <div className="space-y-3" data-testid="resp-kpis">
      {/* Sélecteur de période (flex-wrap → ne déborde jamais) */}
      <div className="flex flex-wrap gap-1.5" data-testid="kpis-periods">
        {KPI_PERIODS.map((p) => (
          <button key={p.value} onClick={() => setPeriod(p.value)} data-testid={`kpis-period-${p.value}`}
            className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${period === p.value ? "bg-[var(--shariti-orange)] text-white" : "bg-[var(--surface-2)] text-muted-ink hover:bg-[#FFE9DC]"}`}>
            {p.label}
          </button>
        ))}
      </div>
      {/* Navigation d'horizon (masquée pour « Tout ») */}
      {period !== "all" && (
        <div className="flex items-center justify-between rounded-xl border border-[var(--border-soft)] bg-white px-2 py-1.5">
          <button onClick={() => setOffset((o) => o - 1)} data-testid="kpis-prev"
            className="grid h-8 w-8 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)]"><ChevronLeft className="h-4 w-4" /></button>
          <span className="truncate px-2 text-center text-xs font-bold text-ink" data-testid="kpis-label">{data?.label || "…"}</span>
          <button onClick={() => data?.can_forward && setOffset((o) => o + 1)} disabled={!data?.can_forward} data-testid="kpis-next"
            className="grid h-8 w-8 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-ink"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !data || (data.members?.length || 0) === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="kpis-empty">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-[rgba(238,114,46,0.10)]"><Users className="h-6 w-6 text-[var(--shariti-orange)]" /></div>
          <h2 className="text-base font-extrabold text-ink">Aucun collaborateur rattaché</h2>
          <p className="mx-auto mt-1 max-w-xs text-sm text-muted-ink">Dès que des collaborateurs seront rattachés à votre équipe, leurs indicateurs apparaîtront ici.</p>
        </div>
      ) : (
        <>
          {/* KPIs AGRÉGÉS DE L'ÉQUIPE */}
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid="kpis-team">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-extrabold text-ink">
              <BarChart3 className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
              <span className="truncate">Équipe · {data.equipe_taille} collaborateur{data.equipe_taille > 1 ? "s" : ""}{data.ex_membres_count ? ` · ${data.ex_membres_count} ancien${data.ex_membres_count > 1 ? "s" : ""}` : ""}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <KpiTile testid="kpi-team-bulletins" label="Bulletins comptés" value={_kpiNum(team.bulletins_comptes)} sub={`${_kpiNum(team.bulletins_signes)} signés`} tone="accent" />
              <KpiTile testid="kpi-team-tauxreel" label="Taux réel (BS/h)" value={_kpiRatio(team.taux_reel)}
                sub={team.heures == null ? "bulletins nets / heure"
                  : (team.heures_formation
                    ? `${_kpiHeures(team.heures_terrain)} terrain · ${_kpiHeures(team.heures_formation)} formation exclue`
                    : `${_kpiHeures(team.heures)} travaillées`)} tone="accent" />
              <KpiTile testid="kpi-team-ratio" label="Taux de validation" value={_kpiPct(team.validation_rate)} sub="bulletins validés" tone="good" />
              <KpiTile testid="kpi-team-don" label="Don moyen" value={_kpiEur(team.don_moyen)} sub={`${_kpiNum(team.don_n)} dons`} />
              <KpiTile testid="kpi-team-age" label="Âge moyen" value={_kpiAge(team.average_age)} sub={team.mature_share != null ? `${_kpiPct(team.mature_share)} > 25 ans` : null} />
              <KpiTile testid="kpi-team-wc" label="Annul. welcome call" value={_kpiPct(team.welcome_call_rate)} sub="cible ≤ 15 %" tone={_wcTone(team.welcome_call_rate)} />
              <KpiTile testid="kpi-team-preattr" label="Pré-attrition ≤3 m" value={attrVal(attr.reliable, attr.pre_attrition_rate)} sub={attrSub(attr.reliable, t3)} tone={attr.reliable && !attrLoading ? _attrTone(attr.pre_attrition_rate, t3) : "default"} />
              <KpiTile testid="kpi-team-attr" label="Attrition 12 m" value={attrVal(attr.reliable, attr.attrition_rate)} sub={attrSub(attr.reliable, t12)} tone={attr.reliable && !attrLoading ? _attrTone(attr.attrition_rate, t12) : "default"} />
              <KpiTile testid="kpi-team-gender" label="Répartition H / F" value={`${team.gender?.H || 0} / ${team.gender?.F || 0}`} sub="hommes / femmes" />
            </div>
            {!data.attrition_configured && (
              <p className="mt-2 flex items-start gap-1 text-[10px] leading-snug text-muted-ink"><Info className="mt-0.5 h-3 w-3 shrink-0" /> L'attrition apparaîtra dès que la source Reporting sera connectée et qu'il y aura assez de recul.</p>
            )}
          </div>

          {/* PODIUM — Top 3 visages de l'équipe (par bulletins) */}
          <TeamPodium members={members} />

          {/* DÉTAIL PAR COLLABORATEUR */}
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]" data-testid="kpis-members">
            <div className="mb-2 text-sm font-extrabold text-ink">Par collaborateur</div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {members.map((m) => (
                <div key={m.rd_id} className={`min-w-0 rounded-xl border p-3 ${m.ex_membre ? "border-dashed border-[var(--border-soft)] bg-[var(--surface-2)]" : "border-[var(--border-soft)]"}`} data-testid={`kpi-member-${m.code_rd || m.rd_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <RecruiterAvatar name={m.rd_name} src={rdPhotoSrc(m)} size={40} className={m.ex_membre ? "opacity-70" : ""} testid={`kpi-member-avatar-${m.code_rd || m.rd_id}`} />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="break-words text-sm font-bold text-ink">{m.rd_name}</span>
                          {m.ex_membre ? <span className="shrink-0 rounded-full bg-[rgba(120,120,120,0.14)] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-muted-ink" data-testid={`kpi-member-ex-${m.code_rd || m.rd_id}`}>Ancien membre</span> : null}
                        </div>
                        {m.code_rd ? <div className="text-[10px] font-semibold text-muted-ink">{m.code_rd}{m.is_independant ? " · indépendant" : ""}</div> : null}
                      </div>
                    </div>
                    <div className="shrink-0 rounded-full bg-[rgba(238,114,46,0.10)] px-2 py-0.5 text-xs font-extrabold text-[var(--shariti-orange)]">{_kpiNum(m.bulletins_comptes)} BS</div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]">
                    <KpiMemberStat label="Taux réel (BS/h)" value={_kpiRatio(m.taux_reel)} tone={m.taux_reel != null ? "good" : "default"} />
                    <KpiMemberStat label="Heures" value={m.heures_formation
                      ? `${_kpiHeures(m.heures)} · dont ${_kpiHeures(m.heures_formation)} form.`
                      : _kpiHeures(m.heures)} />
                    <KpiMemberStat label="Taux de validation" value={_kpiPct(m.validation_rate)} />
                    <KpiMemberStat label="Annul. welcome call" value={_kpiPct(m.welcome_call_rate)} tone={_wcTone(m.welcome_call_rate)} />
                    <KpiMemberStat label="Don moyen" value={_kpiEur(m.don_moyen)} />
                    <KpiMemberStat label="Âge moyen" value={_kpiAge(m.average_age)} />
                    <KpiMemberStat label="Répartition H / F" value={`${m.gender?.H || 0} / ${m.gender?.F || 0}`} />
                    <KpiMemberStat label="Pré-attrition ≤3 m" value={mAttrVal(m.attrition, m.attrition?.pre_attrition_rate)} tone={mAttrTone(m.attrition, m.attrition?.pre_attrition_rate, t3)} />
                    <KpiMemberStat label="Attrition 12 m" value={mAttrVal(m.attrition, m.attrition?.attrition_rate)} tone={mAttrTone(m.attrition, m.attrition?.attrition_rate, t12)} />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[10px] leading-snug text-muted-ink">« Taux réel (BS/h) » = productivité = bulletins nets ÷ heures TERRAIN (les heures de formation sont payées mais EXCLUES de ce ratio, ex. 2 BS en 4 h terrain = 0,50). « Taux de validation » = part des bulletins validés = (approuvés + en attente) ÷ signés (hors annulés/invalides) — ce n'est PAS le « livré ». « Annul. welcome call » = taux d'annulation à l'appel de bienvenue (cible ≤ 15 %, vert = bon). « Répartition H / F » = genre des donateurs recrutés par la personne. Pré-attrition (≤ 3 mois) & attrition (12 mois) issues du Reporting, affichées « — » tant qu'il n'y a pas assez de recul. Les « anciens membres » sont des collaborateurs retirés de l'équipe dont les heures/bulletins de la période comptent toujours.</p>
          </div>
        </>
      )}
    </div>
  );
}



// État vide de l'onglet « Ma perf » du responsable : affiché quand aucune fiche
// collaborateur personnelle n'a pu être rattachée (le responsable ne produit pas de
// bulletins sous son propre nom). Message clair, sans donnée d'autrui.
function RespPerfEmpty({ name }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="resp-perf-empty">
      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-[rgba(238,114,46,0.10)]">
        <UserCog className="h-6 w-6 text-[var(--shariti-orange)]" />
      </div>
      <h2 className="text-base font-extrabold text-ink">Pas encore de perf personnelle</h2>
      <p className="mx-auto mt-1 max-w-xs text-sm text-muted-ink">
        {name ? <b>{name}</b> : "Votre profil"} n'est pas encore relié à une fiche collaborateur.
        Dès que vos propres bulletins seront rattachés, votre niveau, votre série et vos badges
        apparaîtront ici — comme pour vos collaborateurs.
      </p>
    </div>
  );
}

function RespRow({ l, acting, onValider, onSignaler, onEdit, readOnly = false }) {
  const conf = CONF_LABEL[l.confirmation] || CONF_LABEL.sans_reponse;
  const validated = l.resp_validation === "valide";
  const signaled = l.resp_validation === "signale";
  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={`resp-row-${l.code_rd}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2.5">
          <RecruiterAvatar name={l.rd_name} src={rdPhotoSrc(l)} size={38} className="mt-0.5" testid={`resp-row-avatar-${l.code_rd}`} />
          <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="break-words font-extrabold text-ink">{l.rd_name}</span>
            <span className="rounded bg-[var(--surface-2)] px-1 font-mono text-[10px] font-bold text-[var(--shariti-orange)]">{l.code_rd}</span>
            {l.is_independant && <span className="shrink-0 rounded bg-[rgba(93,63,211,0.12)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#5D3FD3]" data-testid={`resp-indep-badge-${l.code_rd}`}>Indépendant</span>}
            {l.is_self && <span className="shrink-0 rounded-full bg-[rgba(238,114,46,0.14)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--shariti-orange)]" data-testid={`resp-self-badge-${l.code_rd}`}>Mes heures</span>}
            {(l.heures_formation ?? 0) > 0 && <span className="inline-flex shrink-0 items-center gap-1 rounded bg-[rgba(15,110,110,0.12)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#0F6E6E]" title="Heures de formation : payées mais exclues du taux BS/h" data-testid={`resp-formation-badge-${l.code_rd}`}><GraduationCap className="h-2.5 w-2.5" />{fmtH(l.heures_formation)} formation</span>}
          </div>
          <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-ink">
            <MapPin className="h-3 w-3 shrink-0" /><span className="break-words">{l.spot_name}</span>
            <span className="ml-1 inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{l.creneau_debut}–{l.creneau_fin}</span>
          </div>
          </div>
        </div>
        <div className="shrink-0 text-right">
          {l.is_independant
            ? <span className="text-[12px] font-bold text-[#5D3FD3]">Hors pointage</span>
            : l.is_absence
              ? <span className="text-[12px] font-bold text-[#9B2226]">à saisir</span>
              : l.en_cours
                ? <span className="text-[12px] font-bold text-[var(--shariti-orange)]">en cours</span>
                : <span className="text-lg font-extrabold tabular-nums text-ink">{fmtH(l.heures)} h</span>}
        </div>
      </div>

      {!l.is_absence && !l.is_independant && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-ink">
          <span>Pointé <b className="text-ink/80">{l.arrivee_pointee || "—"}→{l.depart_pointe || "—"}</b></span>
          {!l.en_cours && (l.arrivee_pointee !== l.arrivee_retenue || l.depart_pointe !== l.depart_retenu) && (
            <span>· Retenu <b className="text-ink/80">{l.arrivee_retenue || "—"}→{l.depart_retenu || "—"}</b></span>
          )}
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${conf.cls}`}>{conf.txt}</span>
        </div>
      )}

      {l.ecretage && l.ecretage.length > 0 && (
        <div className="mt-2 space-y-1" data-testid={`resp-ecretage-${l.code_rd}`}>
          {l.ecretage.map((n, i) => (
            <div key={i} className="flex items-start gap-1 rounded-lg border border-[rgba(47,111,237,0.22)] bg-[rgba(47,111,237,0.06)] px-2 py-1 text-[10px] font-semibold leading-snug text-[#1E4FB0]">
              <Scissors className="mt-0.5 h-3 w-3 shrink-0" />{n.label}
            </div>
          ))}
        </div>
      )}

      {/* Actions responsable : saisie/correction des heures + avis */}
      {l.is_independant ? (
        <div className="mt-3 flex items-center gap-2 rounded-xl bg-[rgba(93,63,211,0.08)] px-3 py-2 text-[12px] font-semibold text-[#5D3FD3]" data-testid={`resp-independant-${l.code_rd}`}>
          <Info className="h-4 w-4 shrink-0" /> Indépendant — non soumis au pointage. Aucune heure à saisir.
        </div>
      ) : l.is_absence ? (
        <div className="mt-3" data-testid={`resp-absence-${l.code_rd}`}>
          <div className="mb-2 flex items-center gap-2 rounded-xl bg-[rgba(214,69,69,0.08)] px-3 py-2 text-[12px] font-bold text-[#9B2226]">
            <AlertCircle className="h-4 w-4 shrink-0" /> Absence à trancher — aucune heure enregistrée
          </div>
          {readOnly ? (
            <div className="flex items-center justify-center gap-2 rounded-lg bg-[var(--surface-2)] px-3 py-2 text-[12px] font-bold text-muted-ink" data-testid={`resp-locked-${l.code_rd}`}>
              <Lock className="h-3.5 w-3.5" /> Saisie désactivée — lecture seule
            </div>
          ) : (
            <Button onClick={onEdit} disabled={acting} data-testid={`resp-saisir-${l.code_rd}`}
              className="w-full bg-[var(--shariti-orange)] font-bold hover:bg-[#d9631f] disabled:opacity-60">
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Pencil className="mr-1 h-4 w-4" /> Saisir les heures</>}
            </Button>
          )}
        </div>
      ) : l.is_self ? (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-[var(--surface-2)] px-3 py-2" data-testid={`resp-self-${l.code_rd}`}>
          <span className="flex items-center gap-2 text-[12px] font-semibold text-muted-ink"><UserRound className="h-4 w-4 shrink-0" /> Vos propres heures.</span>
          {!readOnly && <button onClick={onEdit} disabled={acting} className="shrink-0 text-[12px] font-bold text-[var(--shariti-orange)] hover:underline disabled:opacity-60" data-testid={`resp-corriger-${l.code_rd}`}>Corriger</button>}
        </div>
      ) : (
        <>
          {validated ? (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-[rgba(31,157,85,0.10)] px-3 py-2 text-[12px] font-bold text-[#0F5A2F]" data-testid={`resp-state-${l.code_rd}`}>
              <ShieldCheck className="h-4 w-4" /> Validé{l.resp_validation_at ? ` · ${l.resp_validation_at.slice(11, 16)}` : ""}
            </div>
          ) : signaled ? (
            <div className="mt-3 rounded-xl bg-[rgba(214,69,69,0.08)] px-3 py-2 text-[12px] font-bold text-[#9B2226]" data-testid={`resp-state-${l.code_rd}`}>
              <div className="flex items-center gap-2"><Flag className="h-4 w-4" /> Signalé</div>
              {l.resp_validation_motif && <div className="mt-0.5 text-[11px] font-normal text-muted-ink">« {l.resp_validation_motif} »</div>}
            </div>
          ) : readOnly ? (
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-[var(--surface-2)] px-3 py-2 text-[12px] font-bold text-muted-ink" data-testid={`resp-state-${l.code_rd}`}>
              <Clock className="h-4 w-4" /> En attente de validation
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button onClick={onValider} disabled={acting} data-testid={`resp-valider-${l.code_rd}`}
                className="bg-[#1F9D55] font-bold hover:bg-[#178047] disabled:opacity-60">
                {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1 h-4 w-4" /> Valider</>}
              </Button>
              <Button variant="outline" onClick={onSignaler} disabled={acting} data-testid={`resp-signaler-${l.code_rd}`}
                className="border-[rgba(214,69,69,0.4)] font-bold text-[#9B2226] hover:bg-[rgba(214,69,69,0.06)]">
                <Flag className="mr-1 h-4 w-4" /> Signaler
              </Button>
            </div>
          )}
          {!readOnly && (
            <button onClick={onEdit} disabled={acting} data-testid={`resp-corriger-${l.code_rd}`}
              className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg border border-[var(--border-soft)] px-3 py-1.5 text-[12px] font-bold text-[#8a6600] transition-colors hover:bg-[rgba(185,137,0,0.08)] disabled:opacity-60">
              <Pencil className="h-3.5 w-3.5" /> Corriger les heures
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ————————————————————————————————————————————————————————————————
// Planning du responsable d'équipe — LECTURE SEULE, uniquement SES lignes.
// Objectif : suivre l'avancée, repérer/anticiper les trous (« LIBRE »).
// ————————————————————————————————————————————————————————————————
const RESP_BK_STATUS = {
  ferme: { txt: "Ferme", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
  confirme: { txt: "Confirmé", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]" },
  option: { txt: "Option", cls: "bg-[rgba(185,137,0,0.14)] text-[#8a6600]" },
  annule: { txt: "Annulé", cls: "bg-[var(--surface-2)] text-muted-ink line-through" },
};

// Lien WhatsApp : normalise un numéro FR (0X… → 33X…) vers wa.me. Renvoie null si vide.
const waHref = (phone) => {
  if (!phone) return null;
  let d = String(phone).replace(/[^\d]/g, "");
  if (d.startsWith("0")) d = "33" + d.slice(1);
  return d ? `https://wa.me/${d}` : null;
};

function respPrimeDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(String(iso).length <= 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

// Onglet « Bonus spot » du responsable d'équipe : mêmes bonus de rebooking que la vue RD
// (50 € par emplacement rebooké, versé une fois la visite effectuée). Lecture seule.
function RespPrimesTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let on = true;
    publicApi.get(`/pointage/resp/${token}/primes`)
      .then((r) => { if (on) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [token]);

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--shariti-orange)]" /></div>;
  const items = data?.items || [];
  const per = data?.amount_per || 50;
  const acquired = data?.acquired_count ?? data?.valid_count ?? 0;
  const pending = data?.pending_count || 0;
  const upcomingAmt = data?.upcoming_amount || 0;
  return (
    <div className="space-y-4" data-testid="resp-primes-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[var(--shariti-orange)] to-[var(--shariti-orange-2)] p-4 text-white shadow-[var(--shadow-sm)]">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide opacity-90"><Wallet className="h-4 w-4" /> Mes bonus spot</div>
        <div className="mt-1 flex items-end gap-2">
          <span className="text-4xl font-extrabold" data-testid="resp-primes-total-amount">{data?.total_amount || 0} €</span>
          <span className="mb-1 text-[13px] font-semibold opacity-90">{acquired} acquis{upcomingAmt ? ` · ${upcomingAmt} € à venir` : ""}</span>
        </div>
        <div className="mt-1 text-[11px] opacity-90">{per} € par emplacement · versé une fois la visite effectuée{pending ? ` · ${pending} en attente de validation` : ""}{data?.cancelled_count ? ` · ${data.cancelled_count} annulé(s)` : ""}</div>
      </div>

      {items.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-12 text-center" data-testid="resp-primes-empty">
          <Wallet className="h-7 w-7 text-muted-ink" />
          <p className="mt-2 text-sm font-bold">Aucun bonus pour l'instant</p>
          <p className="mt-1 max-w-xs text-xs text-muted-ink">Chaque emplacement rebooké sur le terrain rapporte {per} € (une fois par magasin, versé après la 1re visite).</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => {
            const dl = (it.dates || []).map(respPrimeDate);
            const up = !it.cancelled && !it.pending && !it.due;   // validé mais visite pas encore passée
            const borderCls = it.cancelled ? "border-[var(--border-soft)] opacity-70" : it.pending ? "border-[rgba(233,184,0,0.55)]" : up ? "border-[rgba(37,99,235,0.35)]" : "border-[var(--border-soft)]";
            const iconWrap = it.cancelled ? "bg-[var(--surface-2)]" : it.pending ? "bg-[rgba(233,184,0,0.18)]" : up ? "bg-[rgba(37,99,235,0.12)]" : "bg-[rgba(31,157,85,0.12)]";
            const euroCls = it.cancelled ? "text-muted-ink line-through" : it.pending ? "text-[#8A6D00]" : up ? "text-[#1D4ED8]" : "text-[#0F5A2F]";
            const euroTxt = it.cancelled ? "annulé" : it.pending ? "à valider" : up ? `${per} €` : `+${per} €`;
            return (
              <div key={it.spot_id || i} className={`flex items-start gap-3 rounded-2xl border bg-white p-3 ${borderCls}`} data-testid={`resp-primes-item-${i}`}>
                <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl ${iconWrap}`}>
                  {it.cancelled ? <XCircle className="h-4 w-4 text-muted-ink" /> : it.pending ? <Hourglass className="h-4 w-4 text-[#8A6D00]" /> : up ? <CalendarDays className="h-4 w-4 text-[#1D4ED8]" /> : <CheckCircle2 className="h-4 w-4 text-[#0F5A2F]" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-bold leading-tight text-ink break-words">{it.spot_name}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-ink">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" />{[it.ville, it.dept ? `(${it.dept})` : null].filter(Boolean).join(" ") || "—"}</span>
                    <span className="inline-flex items-center gap-1 capitalize"><CalendarDays className="h-3 w-3 shrink-0" />{dl.length <= 1 ? (dl[0] || "—") : `${dl.length} dates`}</span>
                  </div>
                  {dl.length > 1 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {dl.map((d, k) => <span key={k} className="rounded-md bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold capitalize text-muted-ink">{d}</span>)}
                    </div>
                  )}
                  {it.pending && <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-[#8A6D00]"><Hourglass className="h-3 w-3" />En attente de validation admin (hors rotation)</div>}
                  {up && <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-[#1D4ED8]"><CalendarDays className="h-3 w-3" />Créditée après la 1re visite</div>}
                </div>
                <span className={`mt-0.5 shrink-0 rounded-full px-2.5 py-1 text-[13px] font-extrabold ${euroCls}`}>
                  {euroTxt}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Rebooking TERRAIN depuis l'espace RESPONSABLE : le responsable autorisé, sur place, a
// l'accord du directeur → il pose la/les date(s), PRÉVISUALISE l'email « Je confirme »
// (identique aux CDP/RD), puis l'envoie. Le rebooking lui est CRÉDITÉ (prime 50 €).
// Verrou anti-télescopage : si un collègue a déjà rebooké ce spot (visite à venir), on
// désactive le bouton. Modale en 2 étapes : calendrier → aperçu → envoi.
function RespRebookDialog({ token, bookingId, spotName, lock, onDone }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("dates");        // "dates" | "preview"
  const [dates, setDates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const frDay = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" }); } catch { return iso; } };
  const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  const reset = () => { setStep("dates"); setDates([]); setPreview(null); setRecipientEmail(""); setCustomNote(""); };
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim());

  const goPreview = async () => {
    if (!dates.length) { toast.error("Choisissez au moins une date."); return; }
    setLoading(true);
    try {
      const iso = dates.map(toISO).sort();
      const r = await publicApi.post(`/pointage/resp/${token}/rebook/preview`, { booking_id: bookingId, dates: iso, recipient_email: recipientEmail.trim() || undefined, custom_note: customNote.trim() || undefined });
      setPreview(r.data);
      setRecipientEmail((r.data?.recipient || r.data?.recipient_stored || "").trim());
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aperçu impossible. Réessayez.");
    } finally { setLoading(false); }
  };

  const submit = async () => {
    if (!emailValid) { toast.error("Renseignez une adresse email valide pour le directeur du magasin."); return; }
    setSending(true);
    try {
      const iso = dates.map(toISO).sort();
      const r = await publicApi.post(`/pointage/resp/${token}/rebook`, { booking_id: bookingId, dates: iso, send_email: true, recipient_email: recipientEmail.trim(), custom_note: customNote.trim() || undefined });
      const n = r.data?.nb_dates ?? r.data?.count ?? 0;
      const isPending = r.data?.pending;
      const prime = r.data?.prime_total || 0;
      const es = r.data?.email?.status;
      const to = r.data?.recipient;
      if (n > 0) {
        const mailTxt = es === "sent" ? ` Email de confirmation envoyé à ${to}.`
          : (es && String(es).startsWith("skipped")) ? " (aucune adresse — email non envoyé)"
          : es === "failed" ? " (l'email n'a pas pu partir — vérifiez l'adresse)" : "";
        const primeTxt = isPending
          ? " En attente de validation admin (hors rotation) — prime créditée une fois validée."
          : ` +${prime} € pour vous (1 emplacement).`;
        toast.success(`Spot rebooké — ${n} date${n > 1 ? "s" : ""} posée${n > 1 ? "s" : ""}.${primeTxt}${mailTxt}`);
        setOpen(false); reset();
        onDone && onDone();
      } else {
        const why = (r.data?.skipped || [])[0]?.reason;
        toast.info(why ? `Aucune date posée — ${why}.` : "Aucune nouvelle date posée.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebooking impossible. Réessayez.");
    } finally { setSending(false); }
  };

  return (
    <>
      {lock?.locked && !lock?.by_me ? (
        <span
          data-testid="resp-rebook-locked"
          title={`Déjà rebooké par ${lock.by || "un collègue"}${lock.date ? ` — visite prévue le ${frDay(lock.date)}` : ""}. Impossible de le rebooker en même temps.`}
          className="mt-2 inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-semibold text-muted-ink">
          <Lock className="h-3.5 w-3.5" /> Réservé par {lock.by || "un collègue"}
        </span>
      ) : (
        <>
          {lock?.by_me && (
            <span data-testid="resp-rebook-mine"
              className="mt-2 mr-1.5 inline-flex items-center gap-1 rounded-full border border-[rgba(238,114,46,0.4)] bg-[rgba(238,114,46,0.08)] px-2 py-0.5 text-[11px] font-semibold text-[var(--shariti-orange)]">
              <CheckCircle2 className="h-3.5 w-3.5" /> Rebooké par vous
            </span>
          )}
          <button type="button" onClick={() => { reset(); setOpen(true); }} data-testid="resp-rebook-open"
            className="mt-2 inline-flex items-center gap-1 rounded-full border border-[rgba(238,114,46,0.4)] px-2 py-0.5 text-[11px] font-semibold text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.08)]">
            <CalendarPlus className="h-3.5 w-3.5" /> {lock?.by_me ? "Ajouter une date" : "Rebooker ce spot"}
          </button>
        </>
      )}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-h-[88vh] max-w-[380px] overflow-y-auto p-4" data-testid="resp-rebook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CalendarPlus className="h-5 w-5 text-[var(--shariti-orange)]" />
              {step === "dates" ? "Rebooker ce spot" : "Aperçu de l'email"}
            </DialogTitle>
            <DialogDescription className="text-[12px] leading-snug">
              {step === "dates" ? (
                <><span className="font-bold text-ink">{spotName}</span> — le directeur est d'accord&nbsp;? Choisissez la/les date(s). Vous verrez l'email avant l'envoi ; ce rebooking sera <b>crédité à votre nom</b>.</>
              ) : (
                <>Voici l'email qui sera envoyé au magasin. Vérifiez, puis confirmez l'envoi.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {step === "dates" ? (
            <>
              <div className="flex justify-center">
                <Calendar mode="multiple" selected={dates} onSelect={(d) => setDates(d || [])}
                  disabled={{ before: midnight }} weekStartsOn={1} data-testid="resp-rebook-calendar"
                  classNames={{
                    day_selected: "bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f] focus:bg-[#d9631f]",
                    day_today: "ring-1 ring-[var(--shariti-orange)]",
                  }} />
              </div>
              {dates.length > 0 && (
                <div className="rounded-lg bg-[var(--surface-2)] px-3 py-2 text-center text-[12px] font-bold text-ink" data-testid="resp-rebook-count">
                  {dates.length} date{dates.length > 1 ? "s" : ""} sélectionnée{dates.length > 1 ? "s" : ""}
                </div>
              )}
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-ink">Message personnalisé au directeur <span className="font-normal text-muted-ink">(facultatif)</span></label>
                <Textarea value={customNote} onChange={(e) => setCustomNote(e.target.value)} rows={2}
                  maxLength={400} data-testid="resp-rebook-note"
                  placeholder="Ex. Merci encore pour votre accueil, à très vite !"
                  className="resize-none text-[13px]" />
                <div className="mt-0.5 text-right text-[10px] text-muted-ink">{customNote.length}/400</div>
              </div>
              <DialogFooter className="flex-row gap-2 sm:justify-between">
                <Button variant="outline" className="flex-1" onClick={() => setOpen(false)} data-testid="resp-rebook-cancel">Annuler</Button>
                <Button className="flex-1 bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]"
                  onClick={goPreview} disabled={loading || !dates.length} data-testid="resp-rebook-preview-btn">
                  {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                  Prévisualiser
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-2.5">
                {/* Destinataires EN CLAIR — le responsable voit exactement qui recevra
                    l'email et peut corriger l'adresse du directeur si besoin. */}
                <div className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid="resp-rebook-recipients">
                  <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)]">
                    <Send className="h-3.5 w-3.5" /> Qui va recevoir cet email ?
                  </div>
                  <label className="mt-2 block text-[11px] font-semibold text-ink">Directeur du magasin (destinataire)</label>
                  <input type="email" inputMode="email" value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="ex. directeur@magasin.fr" data-testid="resp-rebook-recipient-input"
                    className={`mt-1 w-full rounded-lg border px-2.5 py-2 text-[13px] text-ink outline-none transition-colors focus:ring-2 focus:ring-[rgba(238,114,46,0.35)] ${emailValid ? "border-[var(--border-soft)]" : "border-[#E4A33A] bg-[rgba(233,184,0,0.06)]"}`} />
                  {!recipientEmail.trim() ? (
                    <div className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-[#B4531B]" data-testid="resp-rebook-no-recipient">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      Aucune adresse enregistrée pour ce magasin — saisissez l'email du directeur, sinon il ne recevra pas la confirmation.
                    </div>
                  ) : !emailValid ? (
                    <div className="mt-1 text-[11px] font-semibold text-[#B4531B]">Adresse email invalide.</div>
                  ) : (
                    <div className="mt-1 text-[11px] text-muted-ink">Cette adresse sera aussi enregistrée sur la fiche magasin.</div>
                  )}
                  {preview?.recipient_source === "carnet" && (
                    <div className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-[#0F5A2F]" data-testid="resp-rebook-from-carnet">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      Adresse mémorisée pour ce magasin (pré-remplie automatiquement) — modifiable.
                    </div>
                  )}
                  <div className="mt-2 space-y-0.5 border-t border-dashed border-[var(--border-soft)] pt-2 text-[11px] text-muted-ink">
                    {preview?.cc?.length > 0 && (
                      <div><span className="font-semibold text-ink">En copie :</span> {preview.cc.join(", ")}</div>
                    )}
                    <div><span className="font-semibold text-ink">Expéditeur :</span> {preview?.from_name} &lt;{preview?.from_email}&gt;</div>
                    <div className="capitalize"><span className="font-semibold text-ink">Dates :</span> {preview?.dates_label}</div>
                    {preview?.already_planned?.length > 0 && (
                      <div className="text-[#B4531B]">{preview.already_planned.length} date(s) déjà planifiée(s) seront ignorée(s).</div>
                    )}
                  </div>
                </div>
                <div className="text-[11px] font-semibold text-muted-ink">Aperçu de l'email envoyé :</div>
                <iframe title="Aperçu de l'email" data-testid="resp-rebook-preview-iframe" sandbox=""
                  srcDoc={preview?.html || ""} className="h-[280px] w-full rounded-lg border border-[var(--border-soft)] bg-white" />
              </div>
              <DialogFooter className="flex-row gap-2 sm:justify-between">
                <Button variant="outline" className="flex-1" onClick={() => setStep("dates")} data-testid="resp-rebook-back">Retour</Button>
                <Button className="flex-1 bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]"
                  onClick={submit} disabled={sending || !emailValid} data-testid="resp-rebook-submit">
                  {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                  Confirmer & envoyer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


// Carte d'un créneau (planning responsable). Reprend le style DISCRET de la vue RD :
// « Un souci d'accès ? » et « Équipe du jour » sont REPLIÉS par défaut (petit chevron),
// pour ne pas alourdir le planning. Le responsable conserve les boutons Appeler / WhatsApp
// (contact du booker + de chaque collaborateur) une fois le bloc déplié.
function RespBookingCard({ b, token, onRebooked, readOnly = false }) {
  const [bookerOpen, setBookerOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const st = RESP_BK_STATUS[b.status] || { txt: b.status_label || b.status, cls: "bg-[var(--surface-2)] text-muted-ink" };
  const cancelled = b.status === "annule";
  const team = Array.isArray(b.equipe) ? b.equipe : [];
  return (
    <div className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5" data-testid={`resp-plan-bk-${b.id}`}>
      <div className={`break-words text-[13px] font-extrabold leading-tight ${cancelled ? "text-muted-ink line-through" : "text-ink"}`}>{b.spot_name || "Spot"}</div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
        <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${st.cls}`}>{st.txt}</span>
        {b.spot_address && <span className="inline-flex min-w-0 items-center gap-0.5 text-[10px] text-muted-ink"><MapPin className="h-2.5 w-2.5 shrink-0" /><span className="break-words">{b.spot_address}</span></span>}
      </div>
      {!cancelled && <SpotAuthorizedBy auth={b.authorized} testid={`resp-plan-authorized-${b.id}`} />}
      {!cancelled && (b.mon_creneau_debut || b.mon_creneau_fin) && (
        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[rgba(238,114,46,0.10)] px-2 py-0.5 text-[10px] font-bold text-[var(--shariti-orange)]" data-testid={`resp-plan-moncreneau-${b.id}`}>
          <Clock className="h-2.5 w-2.5" /> Votre créneau&nbsp;: {b.mon_creneau_debut || "—"}–{b.mon_creneau_fin || "—"}
        </div>
      )}

      {/* Instructions du spot (consignes du magasin / directeur) — capital sur le terrain. */}
      {!cancelled && <SpotInstructions text={b.consignes} testid={`resp-plan-instructions-${b.id}`} />}

      {/* « Un souci d'accès ? » — petit lien discret dépliable (identique à la vue RD).
          Contact de la personne qui a booké le spot (ou qui l'a rebooké sur le terrain),
          pour la joindre en cas de refus d'accès à l'accueil. */}
      {!cancelled && b.booker && (
        <div className="mt-2" data-testid={`resp-plan-booker-${b.id}`}>
          <button type="button" onClick={() => setBookerOpen((v) => !v)} data-testid={`resp-plan-booker-toggle-${b.id}`}
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold text-muted-ink transition-colors hover:text-[var(--shariti-orange)]"
            aria-expanded={bookerOpen}>
            <LifeBuoy className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
            Un souci d'accès&nbsp;?
            <ChevronDown className={`h-3 w-3 transition-transform ${bookerOpen ? "rotate-180" : ""}`} />
          </button>
          {bookerOpen && (
            <div className="mt-1.5 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5" data-testid={`resp-plan-booker-panel-${b.id}`}>
              <div className="flex items-center gap-1.5 text-[11px] text-ink">
                <UserRound className="h-3.5 w-3.5 shrink-0 text-muted-ink" />
                <span>Réservé par <span className="font-bold">{b.booker.name}</span></span>
              </div>
              {b.booker.has_phone ? (
                <div className="mt-2 flex gap-2">
                  <a href={`tel:+${b.booker.phone_e164}`} data-testid={`resp-plan-booker-call-${b.id}`}
                    className="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-lg bg-[var(--shariti-orange)] text-[12px] font-bold text-white transition-colors hover:bg-[#d9631f]">
                    <Phone className="h-3.5 w-3.5" /> Appeler
                  </a>
                  {waHref(b.booker.phone_e164) && (
                    <a href={waHref(b.booker.phone_e164)} target="_blank" rel="noreferrer" data-testid={`resp-plan-booker-wa-${b.id}`}
                      className="inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-lg bg-[#1FA855] text-[12px] font-bold text-white transition-colors hover:bg-[#178a45]">
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </a>
                  )}
                </div>
              ) : (
                <div className="mt-1.5 text-[11px] leading-snug text-muted-ink">
                  Numéro non renseigné pour ce contact.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Preuve de confirmation À MONTRER SUR PLACE (accusé signé OU e-mail envoyé). */}
      {!cancelled && b.id && (
        <SpotProofButton spotName={b.spot_name}
          load={() => publicApi.get(`/pointage/resp/${token}/booking/${b.id}/confirmation-proof`).then((r) => r.data)}
          testid={`resp-plan-proof-${b.id}`} />
      )}

      {/* [DEMANDE 08/2026] Rebooking terrain DEPUIS l'espace responsable (si autorisé).
          Le responsable, sur place avec l'accord du directeur, repose la/les date(s) ;
          le rebooking lui est crédité (prime 50 €). Verrou anti-télescopage géré. */}
      {!cancelled && b.can_rebook && !readOnly && (
        <RespRebookDialog token={token} bookingId={b.id} spotName={b.spot_name || "ce spot"}
          lock={b.rebook_lock} onDone={onRebooked} />
      )}

      {/* « Équipe du jour » — repliable comme chez les RD. On garde les boutons Appeler /
          WhatsApp sur chaque collaborateur : le responsable doit pouvoir les joindre. */}
      {!cancelled && team.length > 0 && (
        <div className="mt-2 border-t border-dashed border-[var(--border-soft)] pt-2" data-testid={`resp-plan-equipe-${b.id}`}>
          <button type="button" onClick={() => setTeamOpen((v) => !v)} data-testid={`resp-plan-equipe-toggle-${b.id}`}
            className="flex w-full items-center justify-between gap-1 text-[10px] font-extrabold uppercase tracking-wide text-[var(--shariti-orange)] transition-colors hover:text-[#c85618]"
            aria-expanded={teamOpen}>
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> Équipe du jour · {team.length}</span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${teamOpen ? "rotate-180" : ""}`} />
          </button>
          {teamOpen && (
            <div className="mt-2 space-y-1.5" data-testid={`resp-plan-equipe-panel-${b.id}`}>
              {team.map((m) => {
                const wa = waHref(m.phone);
                return (
                  <div key={m.affectation_id} className="flex items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5" data-testid={`resp-plan-member-${m.affectation_id}`}>
                    <RecruiterAvatar name={m.rd_name} src={rdPhotoSrc(m)} size={34} testid={`resp-plan-member-avatar-${m.affectation_id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="break-words text-[12px] font-bold text-ink">{m.rd_name}</span>
                        {m.population === "independant" && <span className="rounded bg-[rgba(93,63,211,0.12)] px-1 text-[8px] font-bold uppercase tracking-wide text-[#5D3FD3]">Indé</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-ink">
                        {m.code_rd && <span className="font-mono font-bold text-[var(--shariti-orange)]">{m.code_rd}</span>}
                        {(m.creneau_debut || m.creneau_fin) && <span className="inline-flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{m.creneau_debut || "—"}–{m.creneau_fin || "—"}</span>}
                      </div>
                    </div>
                    {m.phone ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <a href={`tel:${m.phone}`} title={`Appeler ${m.rd_name} · ${m.phone}`} data-testid={`resp-plan-call-${m.affectation_id}`}
                          className="grid h-8 w-8 place-items-center rounded-full bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.22)]">
                          <Phone className="h-3.5 w-3.5" />
                        </a>
                        {wa && (
                          <a href={wa} target="_blank" rel="noreferrer" title={`WhatsApp ${m.rd_name}`} data-testid={`resp-plan-wa-${m.affectation_id}`}
                            className="grid h-8 w-8 place-items-center rounded-full bg-[rgba(31,157,85,0.12)] text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.22)]">
                            <MessageCircle className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-muted-ink" title="Aucun numéro renseigné pour ce collaborateur">N° —</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RespPlanning({ token, readOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (w) => {
    setLoading(true);
    try {
      const q = w ? `?week=${w}` : "";
      const r = await publicApi.get(`/pointage/resp/${token}/planning${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Planning indisponible.");
      setData(null);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const shiftWeek = (delta) => {
    if (!data?.monday) return;
    // Ancrage à MIDI local + formatage sur les composantes LOCALES (jamais toISOString/UTC) :
    // en France (UTC+2), minuit local converti en UTC reculait d'un jour et ramenait au même
    // lundi → navigation bloquée sur la semaine courante. Ce calcul est insensible au fuseau.
    const d = new Date(data.monday + "T12:00:00");
    d.setDate(d.getDate() + delta * 7);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    load(`${y}-${m}-${day}`);
  };

  const frDay = (iso, opt) => new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", opt);
  const days = data?.days || [];
  const weekRange = days.length
    ? `${frDay(days[0].date, { weekday: "long", day: "numeric", month: "long" })} – ${frDay(days[days.length - 1].date, { weekday: "long", day: "numeric", month: "long" })}`
    : "";

  // Regroupe UNIQUEMENT les spots PERSONNELS du responsable par date (là où on retrouve
  // son nom). Aucune notion de ligne d'équipe ici : c'est SON planning, jour par jour.
  const byDate = {};
  (data?.bookings || []).forEach((b) => {
    if (!b.date) return;
    (byDate[b.date] = byDate[b.date] || []).push(b);
  });
  const totalSpots = data?.bookings?.length || 0;
  const isFutureWeek = !!data?.is_future_week;

  return (
    <div data-testid="resp-planning">
      {/* Navigation semaine */}
      <div className="mb-3 flex items-center justify-between gap-2 rounded-2xl border border-[var(--border-soft)] bg-white p-2 shadow-[var(--shadow-sm)]">
        <button onClick={() => shiftWeek(-1)} disabled={loading} data-testid="resp-plan-prev"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
        <div className="min-w-0 flex-1 text-center">
          <div className="text-sm font-extrabold text-ink" data-testid="resp-plan-week">Semaine {data?.week_number || "—"}</div>
          <div className="truncate text-[11px] text-muted-ink">{weekRange}</div>
        </div>
        <button onClick={() => shiftWeek(1)} disabled={loading} data-testid="resp-plan-next"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-muted-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => load()} disabled={loading} data-testid="resp-plan-today"
          className="shrink-0 rounded-lg px-2 py-1.5 text-xs font-bold text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)] disabled:opacity-50">Cette semaine</button>
      </div>

      {loading ? (
        <div className="space-y-3"><Skeleton className="h-24 w-full rounded-2xl" /><Skeleton className="h-24 w-full rounded-2xl" /><Skeleton className="h-24 w-full rounded-2xl" /></div>
      ) : (totalSpots === 0 && !isFutureWeek) ? (
        <div className="rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-8 text-center" data-testid="resp-plan-empty">
          <CalendarRange className="mx-auto mb-2 h-8 w-8 text-muted-ink" />
          <h2 className="text-base font-extrabold text-ink">Aucune affectation cette semaine</h2>
          <p className="mt-1 text-sm text-muted-ink">Vous n'êtes affecté(e) à aucun emplacement cette semaine. Utilisez les flèches pour changer de semaine.</p>
        </div>
      ) : (
        <>
          {isFutureWeek && (
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-[rgba(238,114,46,0.30)] bg-[rgba(238,114,46,0.07)] px-3 py-2" data-testid="resp-plan-future-banner">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
              <p className="text-[11px] leading-relaxed text-[#8A3A10]">
                Semaine à venir — anticipez&nbsp;! Les jours marqués <span className="font-bold">« À combler »</span> n'ont pas encore de spot&nbsp;: à vous de trouver un emplacement.
              </p>
            </div>
          )}
          <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid="resp-plan-list">
            <div className="divide-y divide-[var(--border-soft)]">
              {days.map((day) => {
                const bks = byDate[day.date] || [];
                // TROU = jour ouvré SANS spot, uniquement sur une semaine À VENIR (anticipation).
                const isTrou = bks.length === 0 && isFutureWeek && !day.is_sunday && !day.is_holiday;
                return (
                  <div key={day.date} className={`flex items-stretch gap-2 px-3 py-2.5 ${day.is_holiday ? "bg-[rgba(252,219,83,0.10)]" : day.is_sunday ? "bg-[rgba(238,114,46,0.03)]" : ""}`} data-testid={`resp-plan-day-${day.date}`}>
                    <div className="w-[74px] shrink-0 pt-0.5">
                      <div className="text-[13px] font-bold capitalize text-ink">{frDay(day.date, { weekday: "short" }).replace(".", "")} {new Date(day.date + "T00:00:00").getDate()}</div>
                      <div className="text-[10px] capitalize text-muted-ink">{frDay(day.date, { month: "short" }).replace(".", "")}</div>
                      {day.is_holiday && <div className="mt-0.5 text-[9px] font-bold text-[#8a6600]">Férié</div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      {bks.length > 0 ? (
                        <div className="space-y-1.5">
                          {bks.map((b) => <RespBookingCard key={b.id} b={b} token={token} onRebooked={() => load(data?.monday)} readOnly={readOnly} />)}
                        </div>
                      ) : isTrou ? (
                        <div className="grid h-full min-h-[38px] place-items-center rounded-lg border border-dashed border-[rgba(238,114,46,0.45)] bg-[rgba(238,114,46,0.06)]" data-testid={`resp-plan-trou-${day.date}`}>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]"><MapPin className="h-3 w-3" />À combler — trouve un spot</span>
                        </div>
                      ) : (
                        <div className="grid h-full min-h-[38px] place-items-center rounded-lg text-[11px] text-muted-ink" data-testid={`resp-plan-none-${day.date}`}>—</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <p className="mt-5 text-center text-[11px] leading-relaxed text-muted-ink" data-testid="resp-plan-legal">
        Voici VOTRE planning, jour par jour (lecture seule). Sur les semaines à venir, les jours
        « À combler » sont sans spot — anticipez en trouvant un emplacement. Pour toute modification,
        contactez votre CDP / l'administrateur.
      </p>
    </div>
  );
}


